import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LoggerService } from '@cargos/sprintpay-services';
import {
    BillingAddressInput,
    BillingAddressUpdateResult,
    VerifyBillingAddressCreditCardComponent,
} from '@cargos/sprintpay-ui';
import {
    CreditCardVerificationDialogData,
    CreditCardVerificationDialogResult,
} from './models/credit-card-verification.models';

/**
 * @description Dialog component for credit card billing address verification.
 * Uses the `lib-verify-billing-address-credit-card` component from sprintpay-ui
 * to verify and update the billing address for a credit card.
 */
@Component({
    standalone: true,
    selector: 'app-credit-card-verification-dialog',
    imports: [MatDialogModule, VerifyBillingAddressCreditCardComponent],
    templateUrl: './credit-card-verification-dialog.component.html',
    styleUrl: './credit-card-verification-dialog.component.scss',
})
export class CreditCardVerificationDialogComponent implements OnInit, OnDestroy {
    private readonly dialogRef =
        inject<MatDialogRef<CreditCardVerificationDialogComponent, CreditCardVerificationDialogResult>>(MatDialogRef);
    private readonly data = inject<CreditCardVerificationDialogData>(MAT_DIALOG_DATA);
    private readonly loggerService = inject(LoggerService);

    private isResolved = false;

    protected readonly billingAddress: BillingAddressInput = this.mapToBillingAddress();

    public ngOnInit(): void {
        this.loggerService.captureMessage('credit_card_verification_popup_shown', 'info');
    }

    public ngOnDestroy(): void {
        if (!this.isResolved) {
            this.loggerService.captureMessage('credit_card_verification_abandoned', 'info');
        }
    }

    /**
     * @description Maps credit card data to billing address input format.
     */
    private mapToBillingAddress(): BillingAddressInput {
        const creditCard = this.data.creditCardData;
        return {
            token: creditCard.token,
            cardLastFour: creditCard.lastFourDigits,
            cardHolderName: creditCard.cardHolderName ?? '',
            street: creditCard.billingAddress?.street ?? '',
            houseNumberOrName: creditCard.billingAddress?.houseNumberOrName ?? '',
            city: creditCard.billingAddress?.city ?? '',
            postalCode: creditCard.billingAddress?.postalCode ?? '',
            country: creditCard.billingAddress?.country ?? '',
        };
    }

    /**
     * @description Handles the result of the address update operation.
     * Closes the dialog with verified=true if the update was successful.
     */
    public onAddressUpdateResult(result: BillingAddressUpdateResult): void {
        if (result.success) {
            this.isResolved = true;
            this.loggerService.captureMessage('credit_card_verification_completed', 'info');
            this.dialogRef.close({ verified: true });
            return;
        }

        this.loggerService.captureMessage('credit_card_verification_failed', 'warning');
    }

    /**
     * @description Handles the cancel action from the verification component.
     * Closes the dialog with verified=true to allow the flow to continue regardless of verification.
     */
    public onCancel(): void {
        this.isResolved = true;
        this.loggerService.captureMessage('credit_card_verification_skipped', 'info');
        this.dialogRef.close({ verified: true });
    }
}
